// Manual fallback for attaching a Host with a pre-provisioned scoped bearer.
// Normal installs should use the enrollment-token flow: the bundled agent sends
// the endpoint, certificate pin, and one-time bearer through its heartbeat.
import React, { useEffect, useState } from "react";
import { C, F, Btn } from "../brand/apo";
import { api, type Team } from "../api";

const labelStyle: React.CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  color: C.fgFaint,
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: 7,
  display: "block",
};
const inputStyle: React.CSSProperties = {
  width: "100%",
  background: C.surfaceHi,
  border: `1px solid ${C.line}`,
  color: C.fgDark,
  fontFamily: F.ui,
  fontSize: 13.5,
  padding: "9px 12px",
  outline: "none",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

export function EnrollHostForm({ onEnrolled, onCancel }: { onEnrolled: () => void; onCancel: () => void }) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [baseUrl, setBaseUrl] = useState("https://");
  const [controlToken, setControlToken] = useState("");
  const [teamId, setTeamId] = useState("");
  const [tlsFingerprint, setTlsFingerprint] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getTeams().then((r) => setTeams(r.teams)).catch(() => setTeams([]));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.enrollHost({
        baseUrl: baseUrl.trim(),
        controlToken: controlToken.trim(),
        teamId: teamId || undefined,
        tlsFingerprint: tlsFingerprint.trim() || undefined,
      });
      onEnrolled();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div style={{ marginBottom: 16, fontFamily: F.mono, fontSize: 11, color: C.fgFaint, lineHeight: 1.6 }}>
        Prefer a Provision token for new installs. Use this fallback only with a scoped
        <code> shc_live_… </code>bearer created locally on the Host. Admin passwords are never accepted.
      </div>

      <Field label="Host control address">
        <input style={inputStyle} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://100.64.0.33:47990" autoFocus />
      </Field>
      <Field label="Scoped Host control token">
        <input
          style={inputStyle}
          type="password"
          value={controlToken}
          onChange={(e) => setControlToken(e.target.value)}
          placeholder="shc_live_…"
          autoComplete="off"
        />
      </Field>
      <Field label="Team">
        <select style={{ ...inputStyle, appearance: "auto" }} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">Unassigned</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Leaf certificate SHA-256 fingerprint">
        <input
          style={inputStyle}
          value={tlsFingerprint}
          onChange={(e) => setTlsFingerprint(e.target.value)}
          placeholder="sha256:aa…"
          autoComplete="off"
        />
      </Field>

      <div style={{ marginTop: 10, fontFamily: F.mono, fontSize: 11, color: C.fgFaint, lineHeight: 1.6 }}>
        Stillhouse live-probes bearer telemetry before storing the encrypted token. Self-signed hosts require the exact leaf-certificate fingerprint.
      </div>

      {error && (
        <div style={{ marginTop: 14, padding: "10px 12px", background: `${C.rose}14`, border: `1px solid ${C.rose}55`, color: C.rose, fontSize: 12.5, fontFamily: F.mono, lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
        <Btn ghost type="button" onClick={onCancel}>Cancel</Btn>
        <Btn primary type="submit">{submitting ? "Verifying bearer…" : "Attach host"}</Btn>
      </div>
    </form>
  );
}
